/**
 * @name: Osano Consent Bridge
 * @author: Cursor (Agent)
 * @date: 2026-10-13
 * @description: Central Osano ↔ Shopify ↔ GTM consent bridge with a maintainable app-script registry (FragranceMarket).
 * @dependencies: Osano CMP, Shopify consent-tracking-api 0.1
 * @strict-mode: true
 *
 * Public API: window._theme.Consent
 * - isConfigured() / isReady() / isGranted(category) / isDenied(category)
 * - whenGranted(category, callback) → Promise<boolean>
 * - loadDeferredApps(urls)
 * - classifyScript(url)
 *
 * Scripts load only on Osano init / Save. Drawer toggles do nothing until Save.
 * Add new third-party app rules to APP_SCRIPT_RULES below — do not scatter matchers in custom.js.
 * Theme Searchanise getresults/getwidgets APIs stay essential (not gated here).
 */

(() => {
  window.Osano =
    window.Osano ||
    function () {
      window.Osano.data.push(arguments);
    };
  window.Osano.data = window.Osano.data || [];
  window._theme = window._theme || {};

  const SCRIPT_POLL_INTERVAL = 100;
  const SCRIPT_POLL_ATTEMPTS = 50;
  const LIVECHAT_READY_ATTEMPTS = 40;
  const CATEGORIES = ['analytics', 'marketing', 'personalization'];

  /**
   * Single registry for deferred Shopify app / third-party scripts.
   * category: 'essential' | 'analytics' | 'marketing' | 'personalization' | 'skip'
   * critical: load immediately (essential only)
   * match: tested against the script URL
   */
  const APP_SCRIPT_RULES = [
    // skip — unused / blocked until apps removed from Shopify Admin
    { id: 'mlveda', match: /mlveda\.com/i, category: 'skip' },
    { id: 'oxi-social-login', match: /social-login\.oxiapps\.com|oxiapps\.com/i, category: 'skip' },
    { id: 'shopify-etl', match: /prod-shopify-etl\.herokuapp\.com/i, category: 'skip' },
    { id: 'bazaarvoice', match: /bazaarvoice\.com/i, category: 'skip' },

    // essential (always; NoFraud → mmapiws device endpoint)
    { id: 'nofraud', match: /nofraud\.com/i, category: 'essential', critical: true },
    { id: 'klarna', match: /klarna/i, category: 'essential', critical: true },
    {
      id: 'easy-redirects',
      match: /d23dclunsivw3h\.cloudfront\.net|easyredirects|redirect-app\.js/i,
      category: 'essential',
      critical: true,
    },

    // analytics
    { id: 'signifyd', match: /signifyd/i, category: 'analytics' },
    { id: 'lucky-orange', match: /d10lpsik1i8c69\.cloudfront\.net/i, category: 'analytics' },
    { id: 'cloudfront-preload', match: /d2z0bn1jv8xwtk\.cloudfront\.net/i, category: 'analytics' },
    // Searchanise widget bootstrap only — theme getresults/getwidgets stay ungated
    {
      id: 'searchanise-widget',
      match: 'https://searchanise-ef84.kxcdn.com/widgets/shopify/init.js',
      category: 'analytics',
    },

    // marketing
    { id: 'trustpilot', match: /trustpilot/i, category: 'marketing' },
    { id: 'justuno', match: /jst\.ai|justuno|scripttags\.justuno\.com/i, category: 'marketing' },
    { id: 'instafeed', match: /nfcube\.com|instafeed/i, category: 'marketing' },
    { id: 'powr', match: /powr\.io/i, category: 'marketing' },
    { id: 'smile', match: /smile\.io/i, category: 'marketing' },
    { id: 'uppromote', match: /uppromote|d1639lhkj5l89m\.cloudfront\.net/i, category: 'marketing' },
    { id: 'listrak', match: /listrak/i, category: 'marketing' },

    // personalization
    { id: 'livechat', match: /livechatinc\.com|cdn\.livechat/i, category: 'personalization' },
    { id: 'motivator', match: /motivate\.shopapps\.site|motivator\.php/i, category: 'personalization' },
    { id: 'smartwishlist', match: /smartwishlist|webmarked\.net/i, category: 'personalization' },
    { id: 'recomatic', match: /recomaticapp\.com|recomatic/i, category: 'personalization' },
  ];

  /** Theme-owned marketing scripts (not from appsScripts). Empty on FragranceMarket. */
  const THEME_MARKETING_SCRIPTS = [];

  const LISTRAK_MERCHANT_ID = 'hQcA0lXRfUMl';
  const JUSTUNO_ACCOUNT_ID = '97C574A7-F784-4540-9BD2-350105729FB5';

  const hasScriptWithSrc = (url) => {
    if (!url) return false;

    for (const node of document.scripts) {
      const src = node.getAttribute('src') || node.src || '';
      if (src === url || (url.indexOf('signifyd') > -1 && src.indexOf('signifyd') > -1)) return true;
    }

    return false;
  };

  const appendExternalScript = (url, onLoad) => {
    if (!url || hasScriptWithSrc(url)) {
      if (onLoad) onLoad();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = url;

    if (onLoad) script.onload = onLoad;
    document.head.appendChild(script);
  };

  const consentStatus = (isGranted) => (isGranted ? 'granted' : 'denied');

  const getClickElement = (event) => {
    if (event.target instanceof Element) return event.target;
    if (event.target instanceof Node) return event.target.parentElement;
    return null;
  };

  /* =========================================================================
     1. Shared consent helpers
     ========================================================================= */

  const Consent = {
    configured: !!document.getElementById('OsanoConsentConfig'),
    ready: false,
    saved: {
      personalization: null,
      analytics: null,
      marketing: null,
    },
    themeConfig: null,
    /** @type {Map<string, Set<Function>>} */
    grantWaiters: new Map(),

    getThemeConfig() {
      if (this.themeConfig) return this.themeConfig;

      const configNode = document.getElementById('OsanoConsentConfig');
      try {
        this.themeConfig = configNode ? JSON.parse(configNode.textContent) : {};
      } catch (error) {
        this.themeConfig = {};
      }

      return this.themeConfig;
    },

    parseAcceptDeny(value) {
      if (value == null) return null;
      if (value === true || value === 'ACCEPT' || value === 'ACCEPTANCE') return true;
      if (value === false || value === 'DENY' || value === 'DENIED') return false;
      return null;
    },

    unwrapOsanoEvent(source) {
      if (!source) return null;
      if (source.detail) return this.unwrapOsanoEvent(source.detail);
      if (source.consent) return this.unwrapOsanoEvent(source.consent);
      return source;
    },

    readCategoryFromObject(source, category) {
      if (!source || typeof source !== 'object') return null;
      const fromUppercaseKey = this.parseAcceptDeny(source[category.toUpperCase()]);
      if (fromUppercaseKey != null) return fromUppercaseKey;
      return this.parseAcceptDeny(source[category]);
    },

    /**
     * @param {'analytics' | 'marketing' | 'personalization'} category
     * @param {unknown} [osanoEvent]
     * @returns {boolean}
     */
    isGranted(category, osanoEvent) {
      if (!this.configured) return true;

      if (osanoEvent == null && this.saved[category] != null) return this.saved[category];

      const fromEvent = this.readCategoryFromObject(this.unwrapOsanoEvent(osanoEvent), category);
      if (fromEvent != null) return fromEvent;

      const osanoCm = window.Osano && window.Osano.cm;
      if (!osanoCm) return false;
      if (typeof osanoCm[category] === 'boolean') return osanoCm[category];

      if (typeof osanoCm.getConsent === 'function') {
        const fromGetConsent = this.readCategoryFromObject(osanoCm.getConsent(), category);
        if (fromGetConsent != null) return fromGetConsent;
      }

      return Boolean(osanoCm[category]);
    },

    isDenied(category) {
      if (!this.configured) return false;
      if (!this.ready) return false;
      return !this.isGranted(category);
    },

    storeSavedConsent(osanoEvent) {
      this.saved.personalization = this.isGranted('personalization', osanoEvent);
      this.saved.analytics = this.isGranted('analytics', osanoEvent);
      this.saved.marketing = this.isGranted('marketing', osanoEvent);
      this.ready = true;
    },

    setBodyAttributes() {
      if (!document.body) return;

      document.body.setAttribute('data-osano-personalization', consentStatus(this.saved.personalization));
      document.body.setAttribute('data-osano-analytics', consentStatus(this.saved.analytics));
      document.body.setAttribute('data-osano-marketing', consentStatus(this.saved.marketing));
    },

    notifyGrantWaiters() {
      for (const category of CATEGORIES) {
        if (!this.saved[category]) continue;
        const waiters = this.grantWaiters.get(category);
        if (!waiters || !waiters.size) continue;

        for (const callback of [...waiters]) {
          waiters.delete(callback);
          try {
            callback(true);
          } catch (error) {
            console.error('Osano consent waiter failed', error);
          }
        }
      }
    },

    /**
     * @param {'analytics' | 'marketing' | 'personalization'} category
     * @param {(granted: boolean) => void} [callback]
     * @returns {Promise<boolean>}
     */
    whenGranted(category, callback) {
      const invoke = (granted) => {
        if (typeof callback === 'function') callback(granted);
        return granted;
      };

      if (!this.configured) {
        return Promise.resolve(invoke(true));
      }

      if (this.ready) {
        const granted = this.isGranted(category);
        return Promise.resolve(invoke(granted));
      }

      return new Promise((resolve) => {
        const waiter = (granted) => {
          if (typeof callback === 'function') callback(granted);
          resolve(granted);
        };

        if (!this.grantWaiters.has(category)) this.grantWaiters.set(category, new Set());
        this.grantWaiters.get(category).add(waiter);

        const onApplied = () => {
          if (!this.ready) return;
          document.removeEventListener('osano:consent-applied', onApplied);
          if (!this.isGranted(category)) {
            this.grantWaiters.get(category)?.delete(waiter);
            if (typeof callback === 'function') callback(false);
            resolve(false);
          }
        };

        document.addEventListener('osano:consent-applied', onApplied);
      });
    },

    dispatchAppliedEvent() {
      document.dispatchEvent(
        new CustomEvent('osano:consent-applied', {
          bubbles: true,
          detail: {
            analytics: this.saved.analytics,
            marketing: this.saved.marketing,
            personalization: this.saved.personalization,
          },
        })
      );
    },
  };

  /* =========================================================================
     2. App script registry + deferred loader
     ========================================================================= */

  const AppScripts = {
    /** @type {Record<string, string[]>} */
    pendingByCategory: {
      analytics: [],
      marketing: [],
      personalization: [],
    },
    scrollQueued: [],
    hasUserScrolled: false,
    justunoLoaded: false,
    listrakLoaded: false,
    scrollListenerBound: false,

    classify(url) {
      if (!url || typeof url !== 'string') {
        return { id: 'unknown', category: 'marketing', critical: false };
      }

      for (const rule of APP_SCRIPT_RULES) {
        const matched =
          typeof rule.match === 'string' ? url.indexOf(rule.match) === 0 : rule.match.test(url);

        if (!matched) continue;

        return {
          id: rule.id,
          category: rule.category,
          critical: Boolean(rule.critical),
        };
      }

      // Safe default when Osano is on: unknown third-party → marketing
      return { id: 'unknown', category: 'marketing', critical: false };
    },

    stash(url, category) {
      if (!CATEGORIES.includes(category)) return;
      const bucket = this.pendingByCategory[category];
      if (bucket.indexOf(url) === -1) bucket.push(url);

      const theme = window._theme;
      if (category === 'analytics' && /signifyd/i.test(url)) {
        theme.signifydScripts = theme.signifydScripts || [];
        if (theme.signifydScripts.indexOf(url) === -1) theme.signifydScripts.push(url);
      }
      if (category === 'marketing' && /trustpilot/i.test(url)) {
        theme.trustpilotScripts = theme.trustpilotScripts || [];
        if (theme.trustpilotScripts.indexOf(url) === -1) theme.trustpilotScripts.push(url);
      }
    },

    append(url) {
      // Preserve Smart Wishlist init when the deferred script finishes loading
      if (/smartwishlist|webmarked\.net/i.test(url)) {
        appendExternalScript(url, () => {
          if (typeof window._theme.initSmartWishlist === 'function') {
            window._theme.initSmartWishlist();
          }
          if (typeof window.handleWishlistChange === 'function') {
            window.handleWishlistChange();
          }
        });
        return;
      }

      appendExternalScript(url);
    },

    flushCategory(category) {
      const urls = this.pendingByCategory[category] || [];
      this.pendingByCategory[category] = [];
      for (const url of urls) this.append(url);
    },

    flushGrantedCategories() {
      for (const category of CATEGORIES) {
        if (Consent.saved[category]) this.flushCategory(category);
      }
    },

    loadJustunoIfAllowed() {
      if (this.justunoLoaded || !this.hasUserScrolled) return;
      if (Consent.configured && !Consent.saved.marketing) return;

      this.justunoLoaded = true;
      window.ju_num = JUSTUNO_ACCOUNT_ID;
      window.asset_host = '//cdn.jst.ai/';
      (function (i, s, o, g, r, a, m) {
        i[r] =
          i[r] ||
          function () {
            (i[r].q = i[r].q || []).push(arguments);
          };
        a = s.createElement(o);
        m = s.getElementsByTagName(o)[0];
        a.async = 1;
        a.src = g;
        m.parentNode.insertBefore(a, m);
      })(window, document, 'script', window.asset_host + 'vck.js', 'juapp');
    },

    loadListrakIfAllowed() {
      if (this.listrakLoaded) return;
      if (Consent.configured && !Consent.saved.marketing) return;
      if (document.getElementById('ltkSDK')) {
        this.listrakLoaded = true;
        return;
      }

      this.listrakLoaded = true;
      const biJsHost = document.location.protocol === 'https:' ? 'https://' : 'http://';
      const script = document.createElement('script');
      script.id = 'ltkSDK';
      script.async = true;
      script.src = biJsHost + 'cdn.listrakbi.com/scripts/script.js?m=' + LISTRAK_MERCHANT_ID + '&v=1';
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    },

    flushScrollQueue() {
      if (!this.hasUserScrolled) return;

      const urls = this.scrollQueued.slice();
      this.scrollQueued = [];
      for (const url of urls) this.append(url);
      this.loadJustunoIfAllowed();
      this.loadListrakIfAllowed();
    },

    bindScrollLoader() {
      if (this.scrollListenerBound) return;
      this.scrollListenerBound = true;

      const onScroll = () => {
        if (window.pageYOffset <= 10) return;
        this.hasUserScrolled = true;
        this.flushScrollQueue();
        document.removeEventListener('scroll', onScroll);
      };

      document.addEventListener('scroll', onScroll, { passive: true });
    },

    queueForScroll(url) {
      if (this.scrollQueued.indexOf(url) === -1) this.scrollQueued.push(url);
      this.bindScrollLoader();
      if (this.hasUserScrolled) this.flushScrollQueue();
    },

    /**
     * @param {string[]} urls
     */
    loadDeferredApps(urls) {
      if (!Array.isArray(urls) || !urls.length) return;

      for (const url of urls) {
        const rule = this.classify(url);

        if (rule.category === 'skip') continue;

        if (rule.category === 'essential') {
          if (rule.critical) this.append(url);
          else this.queueForScroll(url);
          continue;
        }

        if (!Consent.configured) {
          if (rule.critical) this.append(url);
          else this.queueForScroll(url);
          continue;
        }

        if (Consent.ready && Consent.saved[rule.category]) {
          this.queueForScroll(url);
          continue;
        }

        this.stash(url, rule.category);
      }

      this.bindScrollLoader();
    },

    loadThemeMarketingScripts() {
      if (!Consent.saved.marketing) return;
      for (const url of THEME_MARKETING_SCRIPTS) this.append(url);
    },
  };

  /* =========================================================================
     3. Shopify Customer Privacy
     ========================================================================= */

  const ShopifyPrivacy = {
    lastSyncedSignature: '',
    hasCalledReady: false,

    ensureApi(callback) {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error || !window.Shopify.customerPrivacy) return;
        callback();
      });
    },

    markOsanoReady() {
      if (this.hasCalledReady) return;
      if (!window.Osano || !window.Osano.cm || typeof window.Osano.cm.ready !== 'function') return;

      this.ensureApi(() => {
        if (this.hasCalledReady) return;
        try {
          window.Osano.cm.ready('shopify');
          this.hasCalledReady = true;
          if (window._theme.Consent) window._theme.Consent.shopifyReady = true;
        } catch (error) {
          console.error('Osano must be loaded before initializing the Shopify API.');
        }
      });
    },

    updateTrackingConsent() {
      const osanoCm = window.Osano && window.Osano.cm;
      if (!osanoCm) return;

      const trackingConsent = {
        analytics: Boolean(osanoCm.analytics),
        marketing: Boolean(osanoCm.marketing),
        preferences: Boolean(osanoCm.personalization),
        sale_of_data: !osanoCm.optOut,
      };
      const signature = [
        trackingConsent.analytics,
        trackingConsent.marketing,
        trackingConsent.preferences,
        trackingConsent.sale_of_data,
      ].join('|');
      if (signature === this.lastSyncedSignature) {
        this.markOsanoReady();
        return;
      }

      this.ensureApi(() => {
        window.Shopify.customerPrivacy.setTrackingConsent(trackingConsent, () => {
          ShopifyPrivacy.lastSyncedSignature = signature;
        });
        ShopifyPrivacy.markOsanoReady();
      });
    },
  };

  /* =========================================================================
     4. GTM
     ========================================================================= */

  const Gtm = {
    containerId: 'GTM-N5537BV',
    scriptUrl: 'https://www.googletagmanager.com/gtm.js?id=GTM-N5537BV',
    isLoaded: false,

    updateConsentMode() {
      if (typeof window.gtag !== 'function') return;

      window.gtag('consent', 'update', {
        analytics_storage: consentStatus(Consent.saved.analytics),
        ad_storage: consentStatus(Consent.saved.marketing),
        ad_user_data: consentStatus(Consent.saved.marketing),
        ad_personalization: consentStatus(Consent.saved.marketing),
        personalization_storage: consentStatus(Consent.saved.personalization),
      });
    },

    loadContainer() {
      if (this.isLoaded) return;
      this.isLoaded = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      appendExternalScript(this.scriptUrl);

      if (typeof window.gtag === 'function') {
        window.gtag('config', this.containerId, { send_page_view: false });
      }
    },
  };

  /* =========================================================================
     5. Google Ads (Marketing)
     ========================================================================= */

  const GoogleAds = {
    conversionId: 'AW-861443391',
    scriptUrl: 'https://www.googletagmanager.com/gtag/js?id=AW-861443391',
    isLoaded: false,
    adsBlocker: null,

    removeInjectedScripts() {
      for (const node of [...document.scripts]) {
        if ((node.src || '').indexOf(this.conversionId) > -1) node.remove();
      }
    },

    loadTag() {
      if (this.isLoaded) return;
      this.isLoaded = true;
      appendExternalScript(this.scriptUrl);

      if (typeof window.gtag === 'function') {
        window.gtag('js', new Date());
        window.gtag('config', this.conversionId, {
          allow_enhanced_conversions: true,
          send_page_view: false,
        });
      }
    },

    syncWithMarketingConsent() {
      if (Consent.saved.marketing) {
        if (this.adsBlocker) {
          this.adsBlocker.disconnect();
          this.adsBlocker = null;
        }
        this.loadTag();
        return;
      }

      this.removeInjectedScripts();
      if (this.adsBlocker) return;

      this.adsBlocker = new MutationObserver(() => {
        if (!Consent.saved.marketing) GoogleAds.removeInjectedScripts();
      });
      this.adsBlocker.observe(document.documentElement, { childList: true, subtree: true });
    },
  };

  /* =========================================================================
     6. Trustpilot (Marketing)
     ========================================================================= */

  const TrustpilotConsent = {
    bootstrapUrl: 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
    isBootstrapLoaded: false,
    areAppScriptsLoaded: false,

    hasBootstrapScript() {
      for (const node of document.scripts) {
        const src = node.getAttribute('src') || node.src || '';
        if (src.indexOf('widget.trustpilot.com/bootstrap') > -1) return true;
      }

      return false;
    },

    getAppScriptUrls() {
      const scriptUrls = [];
      const addUrl = (url) => {
        if (typeof url !== 'string' || url.toLowerCase().indexOf('trustpilot') === -1) return;
        if (scriptUrls.indexOf(url) === -1) scriptUrls.push(url);
      };

      const theme = window._theme || {};
      for (const list of [theme.trustpilotScripts, AppScripts.pendingByCategory.marketing]) {
        if (!Array.isArray(list)) continue;
        for (const url of list) addUrl(url);
      }

      if (scriptUrls.length) theme.trustpilotScripts = scriptUrls.slice();
      return scriptUrls;
    },

    loadAppScripts() {
      if (this.areAppScriptsLoaded || !Consent.saved.marketing) return;

      const scriptUrls = this.getAppScriptUrls();
      if (!scriptUrls.length) return;

      for (const scriptUrl of scriptUrls) {
        appendExternalScript(scriptUrl);
      }

      this.areAppScriptsLoaded = scriptUrls.some((scriptUrl) => hasScriptWithSrc(scriptUrl));
    },

    loadAppScriptsWhenReady() {
      this.loadAppScripts();
      if (this.areAppScriptsLoaded) return;

      let attempts = 0;
      const tryLoad = () => {
        TrustpilotConsent.loadAppScripts();
        attempts += 1;
        if (!TrustpilotConsent.areAppScriptsLoaded && attempts < SCRIPT_POLL_ATTEMPTS) {
          window.setTimeout(tryLoad, SCRIPT_POLL_INTERVAL);
        }
      };

      window.setTimeout(tryLoad, SCRIPT_POLL_INTERVAL);
    },

    loadWidgetsFromDom() {
      const widgets = document.querySelectorAll('.trustpilot-widget');
      if (!widgets.length) return;

      if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
        for (const widget of widgets) {
          try {
            window.Trustpilot.loadFromElement(widget);
          } catch (error) {
            // TrustBox may already be initialized for this node.
          }
        }
      }
    },

    refreshSummaryWidgets() {
      for (const summary of document.querySelectorAll('trustpilot-summary')) {
        if (typeof summary.fetchData === 'function') {
          summary.fetchData();
        }
      }
    },

    loadBootstrap() {
      if (this.isBootstrapLoaded || this.hasBootstrapScript()) {
        this.isBootstrapLoaded = true;
        this.loadWidgetsFromDom();
        this.refreshSummaryWidgets();
        return;
      }

      this.isBootstrapLoaded = true;
      appendExternalScript(this.bootstrapUrl, () => {
        TrustpilotConsent.loadWidgetsFromDom();
        TrustpilotConsent.refreshSummaryWidgets();
      });
    },

    removeTrustboxFrames() {
      for (const iframe of document.querySelectorAll(
        'iframe[src*="widget.trustpilot.com"], iframe[src*="trustpilot.com/trustboxes"]'
      )) {
        iframe.remove();
      }
    },

    removeInjectedScripts() {
      const appUrls = this.getAppScriptUrls();

      for (const node of [...document.scripts]) {
        const src = node.src || node.getAttribute('src') || '';
        if (!src) continue;

        const lower = src.toLowerCase();
        if (lower.indexOf('/assets/trustpilot') > -1) continue;

        const isVendorBootstrap = lower.indexOf('widget.trustpilot.com/bootstrap') > -1;
        const isStashedApp = appUrls.some(
          (url) => typeof url === 'string' && (src === url || lower.indexOf(url.toLowerCase()) > -1)
        );

        if (isVendorBootstrap || isStashedApp) node.remove();
      }

      this.isBootstrapLoaded = false;
      this.areAppScriptsLoaded = false;
    },

    syncWithMarketingConsent() {
      if (Consent.saved.marketing) {
        this.loadAppScriptsWhenReady();
        this.loadBootstrap();
        return;
      }

      this.removeTrustboxFrames();
      this.removeInjectedScripts();
    },
  };

  /* =========================================================================
     7. Analytics (Clarity, Signifyd)
     ========================================================================= */

  const Analytics = {
    clarityProjectId: 'm7unlsbids',
    isClarityLoaded: false,
    isSignifydLoaded: false,

    loadClarity() {
      if (this.isClarityLoaded) return;
      this.isClarityLoaded = true;
      if (window.clarity) return;

      window.clarity =
        window.clarity ||
        function () {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };

      appendExternalScript('https://www.clarity.ms/tag/' + this.clarityProjectId);
    },

    getSignifydScriptUrls() {
      const scriptUrls = [];
      const addUrl = (url) => {
        if (typeof url === 'string' && url.indexOf('signifyd') > -1 && scriptUrls.indexOf(url) === -1) {
          scriptUrls.push(url);
        }
      };

      const shop = window.Shopify && window.Shopify.shop;
      if (shop) addUrl('https://cdn-scripts.signifyd.com/shopify/script-tag.js?shop=' + shop);

      const theme = window._theme || {};
      for (const list of [theme.signifydScripts, AppScripts.pendingByCategory.analytics]) {
        if (!Array.isArray(list)) continue;
        for (const url of list) addUrl(url);
      }

      if (scriptUrls.length) theme.signifydScripts = scriptUrls.slice();
      return scriptUrls;
    },

    loadSignifyd() {
      if (this.isSignifydLoaded || !Consent.saved.analytics) return;

      const scriptUrls = this.getSignifydScriptUrls();
      if (!scriptUrls.length) return;

      for (const scriptUrl of scriptUrls) {
        appendExternalScript(scriptUrl);
      }

      this.isSignifydLoaded = scriptUrls.some((scriptUrl) => hasScriptWithSrc(scriptUrl));
    },

    loadSignifydWhenReady() {
      if (this.isSignifydLoaded) return;

      this.loadSignifyd();
      if (this.isSignifydLoaded) return;

      let attempts = 0;
      const tryLoad = () => {
        Analytics.loadSignifyd();
        attempts += 1;
        if (!Analytics.isSignifydLoaded && attempts < SCRIPT_POLL_ATTEMPTS) {
          window.setTimeout(tryLoad, SCRIPT_POLL_INTERVAL);
        }
      };

      window.setTimeout(tryLoad, SCRIPT_POLL_INTERVAL);
    },
  };

  /* =========================================================================
     8. LiveChat widget
     ========================================================================= */

  const LiveChat = {
    vendorScriptUrl: 'https://cdn.livechatinc.com/tracking.js',
    licenseId: 12834777,
    triggerSelector:
      '[data-livechat-trigger], .btn-chat, .footer-pane-item.support-chat, .support-chat, [data-chat-toggle]',
    vendorWidgetSelector: '#chat-widget-container, iframe[name="chat-widget"]',
    isVendorScriptLoaded: false,
    widgetReadyAttempts: 0,

    getSettings() {
      const themeConfig = Consent.getThemeConfig();
      const themeSettings = window._theme.settings || {};

      return {
        isEnabled: themeConfig.livechatEnable === true || themeSettings.livechat_enable === true,
        widgetType: themeConfig.livechatType || themeSettings.livechat_type,
      };
    },

    isEnabled() {
      return this.getSettings().isEnabled;
    },

    isVendorWidget() {
      return this.getSettings().widgetType === 'default';
    },

    waitForVendorWidget() {
      let attempts = 0;

      const tryInit = () => {
        attempts += 1;

        if (
          window.LiveChatWidget &&
          window.LiveChatWidget._h &&
          typeof window._theme.initChatWidget === 'function'
        ) {
          window._theme.initChatWidget();
          return;
        }

        if (attempts < SCRIPT_POLL_ATTEMPTS) window.setTimeout(tryInit, SCRIPT_POLL_INTERVAL);
      };

      tryInit();
    },

    prepareVendorLiveChat() {
      window.__lc = window.__lc || {};
      window.__lc.license = window.__lc.license || this.licenseId;

      if (typeof window._theme.ensureVendorLiveChatApi === 'function') {
        window._theme.ensureVendorLiveChatApi();
      }
    },

    loadVendorScript() {
      if (this.isVendorScriptLoaded || !this.isEnabled() || !this.isVendorWidget()) return;

      this.isVendorScriptLoaded = true;
      window.__lc = window.__lc || {};
      window.__lc.license = window.__lc.license || this.licenseId;
      this.prepareVendorLiveChat();
      appendExternalScript(this.vendorScriptUrl, () => LiveChat.waitForVendorWidget());
    },

    hideVendorWidget() {
      document.documentElement.classList.add('osano-livechat-blocked');

      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
        try {
          window.LiveChatWidget.call('hide');
          window.LiveChatWidget.call('minimize');
        } catch (error) {
          // Vendor widget may not be ready.
        }
      }

      for (const widget of document.querySelectorAll(this.vendorWidgetSelector)) {
        widget.setAttribute('hidden', '');
        widget.setAttribute('aria-hidden', 'true');
      }
    },

    showVendorWidget() {
      document.documentElement.classList.remove('osano-livechat-blocked');

      for (const widget of document.querySelectorAll(this.vendorWidgetSelector)) {
        widget.removeAttribute('hidden');
        widget.removeAttribute('aria-hidden');
      }
    },

    showFooterChatButton() {
      const footerChat = document.querySelector('.footer-pane-item.support-chat');
      if (!footerChat) return;

      for (const iconOrLabel of footerChat.querySelectorAll('.icon, .content')) {
        iconOrLabel.classList.remove('hide');
      }

      const loader = footerChat.querySelector('.footer-pane-loader');
      if (loader) loader.classList.add('hide');
    },

    closeDialog() {
      const liveChat = document.querySelector('live-chat');
      const dialog = liveChat ? liveChat.querySelector('dialog') : null;
      if (!dialog) return;

      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');

      for (const trigger of liveChat.querySelectorAll('[data-livechat-trigger]')) {
        trigger.setAttribute('aria-hidden', 'false');
      }
    },

    /**
     * @param {boolean} isBlocked
     */
    setConsentBlocked(isBlocked) {
      const liveChat = document.querySelector('live-chat');
      if (!liveChat) return;

      if (typeof window._theme.setLiveChatConsentBlocked === 'function') {
        window._theme.setLiveChatConsentBlocked(isBlocked);
      } else {
        liveChat.classList.toggle('is-consent-blocked', isBlocked);

        const consentPanel = liveChat.querySelector('[data-livechat-consent]');
        const chatList = liveChat.querySelector('[data-livechat-chats]');
        const chatForm = liveChat.querySelector('[data-livechat-form]');
        const chatLoader = liveChat.querySelector('[data-chat-loader]');

        if (isBlocked) {
          if (consentPanel) consentPanel.classList.remove('hidden');
          if (chatList) chatList.classList.add('hidden');
          if (chatForm) chatForm.classList.add('hidden');
          if (chatLoader) chatLoader.classList.add('hidden');
        } else if (consentPanel) {
          consentPanel.classList.add('hidden');
        }
      }

      if (isBlocked || !this.isVendorWidget()) liveChat.classList.remove('hidden');
      else liveChat.classList.add('hidden');
    },

    openConsentDialog() {
      const showDialog = () => {
        if (typeof window._theme.showLiveChatConsent === 'function') {
          window._theme.showLiveChatConsent();
          return;
        }

        const liveChat = document.querySelector('live-chat');
        if (!liveChat) return;

        LiveChat.setConsentBlocked(true);

        const dialog = liveChat.querySelector('dialog');
        if (!dialog) return;

        try {
          if (typeof dialog.show === 'function' && !dialog.open) dialog.show();
          else dialog.setAttribute('open', 'true');
        } catch (error) {
          dialog.setAttribute('open', 'true');
        }

        for (const trigger of liveChat.querySelectorAll('[data-livechat-trigger]')) {
          trigger.setAttribute('aria-hidden', 'true');
        }
      };

      showDialog();
      window.setTimeout(showDialog, 0);
    },

    listenForDeniedClicks() {
      document.addEventListener(
        'click',
        (event) => {
          if (Consent.isGranted('personalization')) return;

          const clickTarget = getClickElement(event);
          if (!clickTarget) return;

          const chatTrigger = clickTarget.closest(LiveChat.triggerSelector);
          if (!chatTrigger) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          LiveChat.hideVendorWidget();
          LiveChat.openConsentDialog();
        },
        true
      );
    },

    /**
     * @param {unknown} osanoEvent
     */
    updateFromSavedConsent(osanoEvent) {
      if (!this.isEnabled()) return;

      if (!document.querySelector('live-chat') && this.widgetReadyAttempts < LIVECHAT_READY_ATTEMPTS) {
        this.widgetReadyAttempts += 1;
        window.setTimeout(() => applySavedConsent(osanoEvent), 50);
        return;
      }

      const dialog = document.querySelector('live-chat dialog');
      const isDialogOpen = Boolean(dialog && (dialog.open || dialog.hasAttribute('open')));

      if (Consent.saved.personalization) {
        this.showVendorWidget();
        this.setConsentBlocked(false);
        this.loadVendorScript();

        if (this.isVendorWidget()) {
          this.closeDialog();
        } else if (isDialogOpen && typeof window._theme.startLiveChatSdk === 'function') {
          window._theme.startLiveChatSdk();
        }

        return;
      }

      this.hideVendorWidget();
      this.setConsentBlocked(true);
      this.closeDialog();
    },
  };

  /* =========================================================================
     9. Marketing section gates (app blocks held in <template>)
     ========================================================================= */

  const MarketingGates = {
    nfcubeBlocker: null,

    isNfcubeUrl(url) {
      return /nfcube\.com|instafeed/i.test(url || '');
    },

    removeNfcubeArtifacts() {
      for (const node of [...document.scripts]) {
        const src = node.getAttribute('src') || node.src || '';
        if (this.isNfcubeUrl(src)) node.remove();
      }

      for (const node of document.querySelectorAll(
        'link[href*="nfcube.com"], link[href*="instafeed"], iframe[src*="nfcube.com"], iframe[src*="instafeed"], [id*="instafeed"], [class*="instafeed"]'
      )) {
        // Keep our gate hosts so they can remount after Accept
        if (node.closest && node.closest('[data-osano-gate-app="instafeed"]')) {
          if (node.matches && node.matches('[data-osano-gated-mounted]')) node.remove();
          continue;
        }
        node.remove();
      }
    },

    bindNfcubeBlocker() {
      if (this.nfcubeBlocker || !Consent.configured) return;
      if (Consent.saved.marketing) return;

      this.nfcubeBlocker = new MutationObserver(() => {
        if (Consent.saved.marketing) return;
        MarketingGates.removeNfcubeArtifacts();
      });
      this.nfcubeBlocker.observe(document.documentElement, { childList: true, subtree: true });
    },

    unbindNfcubeBlocker() {
      if (!this.nfcubeBlocker) return;
      this.nfcubeBlocker.disconnect();
      this.nfcubeBlocker = null;
    },

    /**
     * Mounts [data-osano-gate="marketing"] template content after Marketing consent.
     * Used for Instafeed app blocks in sections/apps.liquid.
     */
    sync() {
      const hosts = document.querySelectorAll('[data-osano-gate="marketing"]');
      const allowMarketing = !Consent.configured || (Consent.ready && Consent.saved.marketing === true);

      if (Consent.configured && Consent.ready && !Consent.saved.marketing) {
        this.removeNfcubeArtifacts();
        this.bindNfcubeBlocker();
      } else {
        this.unbindNfcubeBlocker();
      }

      if (!hosts.length) return;

      for (const host of hosts) {
        const template = host.querySelector(':scope > template[data-osano-gated-content]');
        if (!template) continue;

        const mounted = host.querySelector(':scope > [data-osano-gated-mounted]');

        if (allowMarketing) {
          host.setAttribute('data-osano-gate-open', '');
          if (mounted) continue;

          const wrapper = document.createElement('div');
          wrapper.setAttribute('data-osano-gated-mounted', '');
          wrapper.appendChild(template.content.cloneNode(true));
          host.appendChild(wrapper);
          continue;
        }

        host.removeAttribute('data-osano-gate-open');
        if (mounted) mounted.remove();
      }
    },
  };

  /* =========================================================================
     10. Apply saved consent (init + Save only)
     ========================================================================= */

  /**
   * @param {unknown} [osanoEvent]
   */
  const applySavedConsent = (osanoEvent) => {
    if (!window.Osano || !window.Osano.cm) return;

    Consent.storeSavedConsent(osanoEvent);
    Consent.setBodyAttributes();
    ShopifyPrivacy.updateTrackingConsent();
    Gtm.updateConsentMode();
    LiveChat.showFooterChatButton();

    if (Consent.saved.analytics || Consent.saved.marketing) Gtm.loadContainer();
    GoogleAds.syncWithMarketingConsent();
    TrustpilotConsent.syncWithMarketingConsent();
    if (Consent.saved.analytics) {
      Analytics.loadClarity();
      Analytics.loadSignifydWhenReady();
    }

    AppScripts.flushGrantedCategories();
    AppScripts.loadThemeMarketingScripts();
    if (Consent.saved.marketing) {
      AppScripts.loadJustunoIfAllowed();
      AppScripts.loadListrakIfAllowed();
      AppScripts.flushScrollQueue();
    }

    MarketingGates.sync();
    LiveChat.updateFromSavedConsent(osanoEvent);
    Consent.notifyGrantWaiters();
    Consent.dispatchAppliedEvent();
  };

  /* =========================================================================
     11. Public API — use this instead of reading data-osano-* attributes
     ========================================================================= */

  window._theme.Consent = {
    isConfigured: () => Consent.configured,
    isReady: () => Consent.ready,
    isGranted: (category) => Consent.isGranted(category),
    isDenied: (category) => Consent.isDenied(category),
    whenGranted: (category, callback) => Consent.whenGranted(category, callback),
    classifyScript: (url) => AppScripts.classify(url),
    loadDeferredApps: (urls) => AppScripts.loadDeferredApps(urls),
    shopifyReady: false,
    /** @deprecated Prefer isDenied(); kept for gradual migration */
    getRules: () => APP_SCRIPT_RULES.slice(),
  };

  // Back-compat helpers used across Liquid/JS
  window._theme.isOsanoDenied = (category) => Consent.isDenied(category);
  window._theme.isOsanoGranted = (category) => Consent.isGranted(category);

  /* =========================================================================
     12. Osano bootstrap
     ========================================================================= */

  // Reload after banner/drawer commit so already-injected app scripts (e.g. Instafeed) cannot linger
  const OSANO_CONSENT_COMMIT_SELECTOR = [
    '.osano-cm-save',
    '.osano-cm-accept',
    '.osano-cm-accept-all',
    '.osano-cm-deny',
    '.osano-cm-deny-all',
    '.osano-cm-reject',
    '.osano-cm-reject-all',
    '.osano-cm-button--type_accept',
    '.osano-cm-button--type_deny',
  ].join(', ');
  let hasBoundConsentSavedListener = false;
  let reloadAfterPreferenceSave = false;

  const applySavedConsentThenReload = (osanoEvent) => {
    applySavedConsent(osanoEvent);
    if (!reloadAfterPreferenceSave) return;

    reloadAfterPreferenceSave = false;
    window.setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  document.addEventListener(
    'click',
    (event) => {
      const clickTarget = getClickElement(event);
      if (!clickTarget || !clickTarget.closest(OSANO_CONSENT_COMMIT_SELECTOR)) return;
      reloadAfterPreferenceSave = true;
    },
    true
  );

  const listenForConsentSaved = () => {
    if (hasBoundConsentSavedListener) return;
    if (!window.Osano || !window.Osano.cm || typeof window.Osano.cm.addEventListener !== 'function') {
      return;
    }

    hasBoundConsentSavedListener = true;
    window.Osano.cm.addEventListener('osano-cm-consent-saved', applySavedConsentThenReload);
  };

  LiveChat.listenForDeniedClicks();

  window.addEventListener('load', () => {
    if (Consent.saved.analytics) Analytics.loadSignifyd();
  });

  window.Osano('onInitialized', () => {
    listenForConsentSaved();
    applySavedConsent();
  });
  window.Osano('onConsentSaved', applySavedConsentThenReload);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applySavedConsent());
  } else {
    applySavedConsent();
  }
})();
